import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationAPI } from '../../../utils/api';
import { useNotification } from '../../../context/NotificationContext';

const NotificationHistory = () => {
    const navigate = useNavigate();
    const { showError } = useNotification();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchNotifications();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchNotifications = async () => {
        try {
            const response = await notificationAPI.getNotifications();
            if (response.data.success) {
                setNotifications(response.data.data);
            }
        } catch (error) {
            showError('Failed to fetch notifications');
        } finally {
            setLoading(false);
        }
    };

    const markAsRead = async (id) => {
        try {
            await notificationAPI.markAsRead(id);
            setNotifications(notifications.map(n =>
                n._id === id ? { ...n, is_read: true } : n
            ));
        } catch (error) {
            console.error('Failed to mark as read');
        }
    };

    const deleteNotification = async (id) => {
        try {
            await notificationAPI.deleteNotification(id);
            setNotifications(notifications.filter(n => n._id !== id));
        } catch (error) {
            showError('Failed to delete notification');
        }
    };

    const getTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const seconds = Math.floor((now - date) / 1000);

        if (seconds < 60) return 'Just now';
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours}h ago`;
        return date.toLocaleDateString();
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-dark via-dark to-dark-light text-light p-4 pt-[calc(1.5rem+env(safe-area-inset-top))] pb-[calc(6rem+env(safe-area-inset-bottom))]">
            <div className="max-w-4xl mx-auto">
                {/* Header Section */}
                <div className="flex items-center justify-between mb-8 sticky top-[env(safe-area-inset-top)] z-10 bg-dark/80 backdrop-blur-md py-4 -mx-4 px-4 border-b border-gold/10">
                    <div className="flex items-center">
                        <button
                            onClick={() => navigate(-1)}
                            className="mr-3 p-2 bg-gray-800/50 rounded-full text-gold hover:bg-gold/20 transition-all border border-transparent hover:border-gold/30"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <h1 className="text-2xl font-bold bg-gradient-to-r from-gold to-yellow-300 bg-clip-text text-transparent">
                            Notifications
                        </h1>
                    </div>
                    {notifications.length > 0 && (
                        <div className="flex items-center space-x-2">
                            {notifications.some(n => !n.is_read) && (
                                <span className="flex items-center px-3 py-1 rounded-full bg-gold/10 text-gold text-xs font-medium border border-gold/20 shadow-[0_0_10px_rgba(255,215,0,0.1)]">
                                    <span className="w-1.5 h-1.5 rounded-full bg-gold mr-1.5 animate-pulse"></span>
                                    {notifications.filter(n => !n.is_read).length} New
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* Content Section */}
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-32 space-y-4">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t-2 border-gold border-r-2 border-r-transparent border-l-2 border-l-transparent"></div>
                        <p className="text-gray-400 text-sm animate-pulse">Loading updates...</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {notifications.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-24 px-6 bg-gray-900/40 rounded-3xl border border-gray-800/50 backdrop-blur-sm">
                                <div className="w-20 h-20 bg-gradient-to-br from-gray-800 to-gray-900 rounded-full flex items-center justify-center mb-6 shadow-inner/50">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-gray-300 mb-2">You're all caught up!</h3>
                                <p className="text-gray-500 text-center text-sm max-w-xs leading-relaxed">
                                    There are no new notifications or alerts at this time.
                                </p>
                            </div>
                        ) : (
                            <div className="flex flex-col space-y-3 relative">
                                {/* Vertical connection line */}
                                <div className="absolute left-[23px] top-6 bottom-6 w-px bg-gradient-to-b from-gold/30 via-gold/10 to-transparent hidden sm:block"></div>

                                {notifications.map((notification, index) => (
                                    <div
                                        key={notification._id}
                                        onClick={() => !notification.is_read && markAsRead(notification._id)}
                                        className={`relative group flex gap-4 p-5 rounded-2xl cursor-pointer transition-all duration-300 overflow-hidden
                                            ${!notification.is_read
                                                ? 'bg-gradient-to-r from-gray-800 to-gray-900 border border-gold/40 shadow-[0_4px_20px_rgba(0,0,0,0.4)]'
                                                : 'bg-gray-900/60 border border-gray-800 hover:bg-gray-800 hover:border-gray-700 shadow-md'
                                            }`}
                                    >
                                        {/* Unread Left Glow Accent */}
                                        {!notification.is_read && (
                                            <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-gold to-yellow-500 shadow-[0_0_10px_rgba(255,215,0,0.5)]"></div>
                                        )}

                                        {/* Icon Container */}
                                        <div className={`shrink-0 w-12 h-12 rounded-full flex items-center justify-center border z-10 
                                            ${!notification.is_read
                                                ? 'bg-dark border-gold/50 shadow-[0_0_15px_rgba(255,215,0,0.15)] text-gold'
                                                : 'bg-gray-800 border-gray-700 text-gray-400'
                                            }`}>
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                            </svg>
                                        </div>

                                        {/* Content Block */}
                                        <div className="flex-1 min-w-0 flex flex-col justify-center">
                                            <div className="flex justify-between items-start mb-1.5">
                                                <h3 className={`font-semibold text-base truncate pr-6 ${!notification.is_read ? 'text-white' : 'text-gray-300'}`}>
                                                    {notification.title}
                                                </h3>
                                                <span className="shrink-0 text-xs font-medium text-gray-500 bg-black/20 px-2 py-0.5 rounded-full whitespace-nowrap">
                                                    {getTimeAgo(notification.createdAt)}
                                                </span>
                                            </div>
                                            <p className={`text-sm leading-relaxed ${!notification.is_read ? 'text-gray-300 font-medium' : 'text-gray-500'}`}>
                                                {notification.message}
                                            </p>
                                        </div>

                                        {/* Delete Button overlay on hover */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                deleteNotification(notification._id);
                                            }}
                                            className="absolute top-4 right-4 p-2 bg-yellow-400/10 text-yellow-400 hover:bg-yellow-400 hover:text-white rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200"
                                            title="Delete Notification"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificationHistory;
